import React, { useState, TextareaHTMLAttributes, useEffect } from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { css } from '@emotion/react';
import {
  colors,
  mixins,
  spaces,
  text,
} from '../../../core/styles/theme/default';
import ErrorMessage from '../ErrorMessage';

const StyledLabel = styled.label`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
`;
const StyledSpan = styled.span(
  ({ required, hideLabel }: { required: boolean; hideLabel: boolean }) => {
    const requiredStyles = css`
      &:after {
        content: '*';
        ${text.p4};
        display: inline-block;
        margin-left: ${rem(4)};
        color: ${colors.nightBlue500};
      }
    `;

    return css`
      ${text.p4};
      padding: 0 ${rem(2)};
      color: ${colors.nightBlue500};
      background-color: ${colors.white};
      pointer-events: none;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: calc(100% - 2 * ${rem(16)});
      overflow: hidden;
      padding-bottom: ${rem(4)};

      ${required && requiredStyles}
      ${hideLabel &&
      css`
        ${mixins.visuallyHidden};
      `}
    `;
  },
);

const StyledTextarea = styled.textarea(
  ({ hasError }: { hasError: boolean }) => css`
    ${text.p3};
    padding: ${rem(spaces.delta)};
    resize: none;
    width: 100%;
    border: 1px solid ${colors.grue300};
    border-radius: ${rem(2)};
    color: ${colors.grey700};
    background-color: ${colors.white};
    outline: none;
    box-sizing: border-box;
    min-height: ${rem(58)};

    &:disabled {
      background-color: ${colors.grey300};
    }

    ${hasError &&
    css`
      border-color: ${colors.error};
    `};

    &:hover,
    &:focus {
      text-overflow: unset;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.06),
        0px 4px 6px rgba(0, 0, 0, 0.1);
    }
  `,
);
const StyledDiv = styled.div`
  display: flex;
  justify-content: space-between;
`;
const StyledErrorMessage = styled(ErrorMessage)`
  flex-basis: 100%;
`;
const StyledTextareaCount = styled.div`
  ${text.l1};
  color: ${colors.grey500};
  text-align: right;
  flex-basis: 100%;
  margin-top: ${rem(8)};
`;

const Textarea = ({
  errorText,
  required,
  onChangeRequest,
  ...rest
}: TextareaProps) => {
  const { value, maxLength, label, hideLabel } = rest;
  const [textareaVal, setTextareaVal] = useState(value || '');
  const [textareaValLength, setTextareaValLength] = useState(0);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = event.target;
    const computed = window.getComputedStyle(input);

    input.style.height = '58px';

    const height =
      input.scrollHeight +
      parseInt(computed.getPropertyValue('border-top-width'), 10) +
      parseInt(computed.getPropertyValue('border-bottom-width'), 10);

    input.style.height = `${height}px`;

    if (maxLength) {
      /* istanbul ignore else */
      if (input.value.length <= maxLength) {
        setTextareaVal(input.value);
        setTextareaValLength(input.value.length);
      }
    } else {
      setTextareaVal(input.value);
    }

    onChangeRequest?.(event);
  };

  useEffect(() => {
    setTextareaVal(value ?? '');
  }, [value]);

  return (
    <StyledLabel>
      <StyledSpan hideLabel={!!hideLabel} required={!!required}>
        {label}
      </StyledSpan>
      <StyledTextarea
        value={textareaVal}
        hasError={!!errorText}
        onChange={handleChange}
        {...rest}
      />
      {(errorText || maxLength) && (
        <StyledDiv>
          {errorText && <StyledErrorMessage text={errorText} />}
          {maxLength && (
            <StyledTextareaCount>{`${
              maxLength - textareaValLength
            } / ${maxLength}`}</StyledTextareaCount>
          )}
        </StyledDiv>
      )}
    </StyledLabel>
  );
};

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  hideLabel?: boolean;
  required?: boolean;

  /** Error Text to be shown */
  errorText?: string;
  /** Optional Callback fired when the Textarea changes. For example usefull to get the value of the Textarea */
  onChangeRequest?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

export default Textarea;
